import { Vestiging } from "@/entities/vestiging/domain";
import { ActionsDropdown, Badge, DropdownMenuItem } from "@/shared/components";
import { useIntervalRerender } from "@/shared/hooks";
import { useState } from "react";
import { VerlengenOpeningstijdenDialog, VerzoekVerlengenOpeningstijdenDialog } from "../../shared";
import { Handlers } from "./columns-factory";

type BadgeColProps = {
    vestiging: Vestiging;
};

export function BadgeCol({ vestiging }: Readonly<BadgeColProps>) {
    useIntervalRerender(60); // re-render every minute so minutesUntilClose is live updating

    if (!Vestiging.canRequestStart(vestiging)) {
        return (
            <Badge variant="gray-subtle" className="text-xs">
                In uitvoering
            </Badge>
        );
    }

    const minutesUntilClose = Vestiging.getMinutesUntilClose(vestiging);
    const closedNow = Vestiging.isClosed(vestiging);

    if (vestiging.beheerVestiging.isNsRetailVestiging && vestiging.currentOpeningstijden.aangepasteOpenTot) {
        return <Badge variant="blue-subtle">Openingstijd aangepast</Badge>;
    }

    if (!vestiging.beheerVestiging.isNsRetailVestiging && vestiging.currentOpeningstijden.aangepasteOpenTot) {
        return (
            <Badge variant="blue-subtle">
                Langere openingstijd voorgesteld:{" "}
                <span className="font-bold">&nbsp;{vestiging.currentOpeningstijden.aangepasteOpenTot}</span>
            </Badge>
        );
    }

    if (closedNow) {
        return (
            <Badge variant="red-subtle" className="text-xs">
                Gesloten
            </Badge>
        );
    }

    if (minutesUntilClose != null && minutesUntilClose <= 65) {
        return <Badge variant="yellow-subtle">Sluit over {minutesUntilClose} minuten</Badge>;
    }
}

type MenuColProps = Handlers & {
    vestiging: Vestiging;
};

export function MenuCol({ vestiging, onUpdateOpeningstijd }: Readonly<MenuColProps>) {
    const [dialogOpen, setDialogOpen] = useState(false);

    if (!Vestiging.canModifyOpeningHours(vestiging, "nieuweAanvraag")) return null;

    return (
        <div className="flex justify-end">
            <ActionsDropdown data-testid="vestiging-nieuwe-aanvraag">
                <DropdownMenuItem onClick={() => setDialogOpen(true)}>Openingstijd verlengen</DropdownMenuItem>
            </ActionsDropdown>
            {vestiging.beheerVestiging.isNsRetailVestiging ? (
                <VerlengenOpeningstijdenDialog
                    vestiging={vestiging}
                    onCancel={() => setDialogOpen(false)}
                    onUpdateOpeningstijd={onUpdateOpeningstijd}
                    onOpenChange={setDialogOpen}
                    open={dialogOpen}
                    key={dialogOpen ? `${vestiging.beheerVestiging.id}-open` : `${vestiging.beheerVestiging.id}-closed`} // this key makes sure we don't see stale form data / validation messages when re-opening the dialog
                />
            ) : (
                <VerzoekVerlengenOpeningstijdenDialog
                    vestiging={vestiging}
                    onCancel={() => setDialogOpen(false)}
                    onUpdateOpeningstijd={onUpdateOpeningstijd}
                    onOpenChange={setDialogOpen}
                    open={dialogOpen}
                    key={dialogOpen ? `${vestiging.beheerVestiging.id}-open` : `${vestiging.beheerVestiging.id}-closed`} // this key makes sure we don't see stale form data / validation messages when re-opening the dialog
                />
            )}
        </div>
    );
}
